import React, { useState, useEffect, useCallback } from 'react';
import { Fighter, getFighterAttack, getFighterDefense, getFighterRange, canFighterAttack, getAttackCost, getDefenseCost, createFighter, getFighterSpritePath, getFighterPassiveDefense, getFighterActiveDefense, getFighterMaxHealth } from '../types/fighter';
import { GameCell, canMoveTo, getMoveDistance, generateRandomMap, generateBackgroundLayer, canAttackInLine, calculatePath, getMovementCost } from '../types/game';
import { aiTurn } from '../utils/ai';
import { motion, useAnimation } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { useTranslation } from '../hooks/useTranslation';
import './GameBoard.css';

interface GameBoardProps {
  playerFighters: Fighter[];
  mapSize: { width: number; height: number };
  onGameOver: (winner: 'player' | 'ai', fighters: Fighter[]) => void;
  aiFighters?: Fighter[]; // Опциональные враги для рейдов
}

export const GameBoard: React.FC<GameBoardProps> = ({ playerFighters, mapSize, onGameOver, aiFighters: providedAiFighters }) => {
  const MAP_WIDTH = mapSize.width;
  const MAP_HEIGHT = mapSize.height;
  const BORDER_SIZE = 6; // Размер фонового слоя вокруг игрового поля

  const [map, setMap] = useState<GameCell[][]>(() => generateRandomMap(MAP_WIDTH, MAP_HEIGHT));
  const [backgroundLayer, setBackgroundLayer] = useState<GameCell[][]>(() => generateBackgroundLayer(MAP_WIDTH, MAP_HEIGHT, BORDER_SIZE));
  const [fighters, setFighters] = useState<Fighter[]>([]);
  const [currentFighterIndex, setCurrentFighterIndex] = useState(0);
  const [selectedCell, setSelectedCell] = useState<{ x: number; y: number } | null>(null);
  const [isAiTurn, setIsAiTurn] = useState(false);
  const [hoveredFighterId, setHoveredFighterId] = useState<string | null>(null);
  const [hoveredEnemy, setHoveredEnemy] = useState<{ fighter: Fighter; damage: number } | null>(null);
  const [battleLog, setBattleLog] = useState<Array<{ attacker: string; target: string; damage: number; timestamp: number }>>([]);
  const [isMoving, setIsMoving] = useState(false);
  const { usePotion, potions } = useGameStore();
  const t = useTranslation();


  // Attack Effects State
  const [damageIndicators, setDamageIndicators] = useState<Array<{ id: number; x: number; y: number; damage: number }>>([]);
  const [shakingCell, setShakingCell] = useState<{ x: number; y: number } | null>(null);
  const [flashingFighterId, setFlashingFighterId] = useState<string | null>(null);

  const [isQueueCollapsed, setIsQueueCollapsed] = useState(true);
  const [isLogCollapsed, setIsLogCollapsed] = useState(true);
  const [showCoords, setShowCoords] = useState(false);
  const [zoom, setZoom] = useState(0.8);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapElementRef = React.useRef<HTMLDivElement>(null);
  const mapControls = useAnimation();
  const currentTransformRef = React.useRef<{ x: number; y: number; scale: number }>({ x: 0, y: 0, scale: zoom });

  // Функция для вычисления ограничений перетаскивания
  const getDragConstraints = useCallback((): { left: number; right: number; top: number; bottom: number } => {
    if (!containerRef.current) return { left: 0, right: 0, top: 0, bottom: 0 };
    const container = containerRef.current;
    const cellSize = 105;
    const gap = 2;
    const padding = 5; // padding из background-grid
    const bgWidth = (MAP_WIDTH + BORDER_SIZE * 2) * (cellSize + gap) + padding * 2;
    const bgHeight = (MAP_HEIGHT + BORDER_SIZE * 2) * (cellSize + gap) + padding * 2;
    // Используем актуальный зум из ref, если доступен
    const currentZoom = currentTransformRef.current?.scale || zoom;
    const scaledWidth = bgWidth * currentZoom;
    const scaledHeight = bgHeight * currentZoom;

    // Если карта меньше контейнера, не позволяем двигать её
    if (scaledWidth <= container.offsetWidth && scaledHeight <= container.offsetHeight) {
      return { left: 0, right: 0, top: 0, bottom: 0 };
    }

    // Вычисляем границы: карта не должна выходить за пределы последней видимой клетки фона
    const minX = container.offsetWidth - scaledWidth;
    const minY = container.offsetHeight - scaledHeight;

    return {
      left: minX,
      right: 0,
      top: minY,
      bottom: 0
    };
  }, [MAP_WIDTH, MAP_HEIGHT, BORDER_SIZE, zoom]);

  // Создаем объект ограничений для framer-motion
  const dragConstraintsValue = React.useMemo(() => {
    return getDragConstraints();
  }, [getDragConstraints]);

  // Функция для ограничения позиции карты в пределах видимости
  const constrainMapPosition = useCallback((x: number, y: number, currentZoom?: number) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const container = containerRef.current;
    const cellSize = 105;
    const gap = 2;
    const padding = 5;
    const bgWidth = (MAP_WIDTH + BORDER_SIZE * 2) * (cellSize + gap) + padding * 2;
    const bgHeight = (MAP_HEIGHT + BORDER_SIZE * 2) * (cellSize + gap) + padding * 2;
    const scale = currentZoom || currentTransformRef.current.scale || zoom;
    const scaledWidth = bgWidth * scale;
    const scaledHeight = bgHeight * scale;

    // Если карта меньше контейнера, центрируем её
    if (scaledWidth <= container.offsetWidth && scaledHeight <= container.offsetHeight) {
      return { x: 0, y: 0 };
    }

    // Вычисляем границы: карта не должна выходить за пределы последней видимой клетки фона
    // minX/minY - максимальное смещение влево/вверх (когда правая/нижняя граница карты = граница контейнера)
    // maxX/maxY = 0 - максимальное смещение вправо/вниз (когда левая/верхняя граница карты = граница контейнера)
    const minX = container.offsetWidth - scaledWidth;
    const minY = container.offsetHeight - scaledHeight;
    const maxX = 0;
    const maxY = 0;

    // Строго ограничиваем позицию
    const constrainedX = Math.max(minX, Math.min(maxX, x));
    const constrainedY = Math.max(minY, Math.min(maxY, y));

    return {
      x: constrainedX,
      y: constrainedY
    };
  }, [MAP_WIDTH, MAP_HEIGHT, BORDER_SIZE, zoom]);

  // Состояние для подсветки клеток движения
  const [movementHighlights, setMovementHighlights] = useState<Map<string, 'move-with-attack' | 'move-max' | 'blocked'>>(new Map());

  // Отслеживание перетаскивания карты для предотвращения ложных кликов
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const dragStartPositionRef = React.useRef<{ x: number; y: number } | null>(null);

  // Отслеживание предыдущего индекса для сброса щита при смене юнита
  const prevFighterIndexRef = React.useRef<number>(-1);

  // Состояние для отмены хода
  const [turnStartState, setTurnStartState] = React.useState<{
    fighter: Fighter;
    position: { x: number; y: number };
    stamina: number;
    isDefending: boolean;
  } | null>(null);
  const [lastAction, setLastAction] = React.useState<'move' | 'attack' | null>(null);
  const [attackedEnemyState, setAttackedEnemyState] = React.useState<{ id: string; health: number } | null>(null);

  const handleZoomIn = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!containerRef.current) return;

    const container = containerRef.current;
    const centerX = container.offsetWidth / 2;
    const centerY = container.offsetHeight / 2;

    // Получаем текущую позицию и зум из ref
    const currentX = currentTransformRef.current.x;
    const currentY = currentTransformRef.current.y;
    const currentZoom = currentTransformRef.current.scale;
    const newZoom = Math.min(currentZoom + 0.2, 1.5);

    // Вычисляем новую позицию для сохранения центра на месте
    const scaleRatio = newZoom / currentZoom;
    const newX = centerX - (centerX - currentX) * scaleRatio;
    const newY = centerY - (centerY - currentY) * scaleRatio;

    // Применяем ограничения к новой позиции
    const constrained = constrainMapPosition(newX, newY, newZoom);

    // Обновляем ref
    currentTransformRef.current = { x: constrained.x, y: constrained.y, scale: newZoom };

    mapControls.start({
      scale: newZoom,
      x: constrained.x,
      y: constrained.y,
      transition: { type: 'spring', stiffness: 300, damping: 30 }
    });

    setZoom(newZoom);
  };

  const handleZoomOut = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!containerRef.current) return;

    const container = containerRef.current;
    const centerX = container.offsetWidth / 2;
    const centerY = container.offsetHeight / 2;

    // Получаем текущую позицию и зум из ref
    const currentX = currentTransformRef.current.x;
    const currentY = currentTransformRef.current.y;
    const currentZoom = currentTransformRef.current.scale;
    const newZoom = Math.max(currentZoom - 0.2, 0.4);

    // Вычисляем новую позицию для сохранения центра на месте
    const scaleRatio = newZoom / currentZoom;
    const newX = centerX - (centerX - currentX) * scaleRatio;
    const newY = centerY - (centerY - currentY) * scaleRatio;

    // Применяем ограничения к новой позиции
    const constrained = constrainMapPosition(newX, newY, newZoom);

    // Обновляем ref
    currentTransformRef.current = { x: constrained.x, y: constrained.y, scale: newZoom };

    mapControls.start({
      scale: newZoom,
      x: constrained.x,
      y: constrained.y,
      transition: { type: 'spring', stiffness: 300, damping: 30 }
    });

    setZoom(newZoom);
  };

  const triggerAttackEffects = (targetX: number, targetY: number, targetId: string, damage: number) => {
    // 1. Shake Effect
    setShakingCell({ x: targetX, y: targetY });
    setTimeout(() => setShakingCell(null), 400);

    // 2. Flash Effect
    setFlashingFighterId(targetId);
    setTimeout(() => setFlashingFighterId(null), 300);

    // 3. Floating Damage
    if (damage > 0) {
      const id = Date.now();
      setDamageIndicators(prev => [...prev, { id, x: targetX, y: targetY, damage }]);
      setTimeout(() => {
        setDamageIndicators(prev => prev.filter(i => i.id !== id));
      }, 1000);
    }
  };

  const centerOnFighter = (fighter: Fighter) => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const cellSize = 105;
    const gap = 2;
    const padding = 5;
    // Используем актуальный зум из ref
    const currentZoom = currentTransformRef.current?.scale || zoom;

    // Вычисляем центр клетки бойца в координатах сетки (с учетом фонового слоя)
    const fighterCenterX = (fighter.x + BORDER_SIZE) * (cellSize + gap) + cellSize / 2 + padding;
    const fighterCenterY = (fighter.y + BORDER_SIZE) * (cellSize + gap) + cellSize / 2 + padding;

    // Вычисляем смещение для центрирования
    const centerOffsetX = (container.offsetWidth / 2) - (fighterCenterX * currentZoom);
    const centerOffsetY = (container.offsetHeight / 2) - (fighterCenterY * currentZoom);

    // Ограничиваем позицию через функцию constrainMapPosition
    const constrained = constrainMapPosition(centerOffsetX, centerOffsetY, currentZoom);

    // Обновляем ref
    currentTransformRef.current = { x: constrained.x, y: constrained.y, scale: currentZoom };

    mapControls.start({
      x: constrained.x,
      y: constrained.y,
      transition: { type: 'spring', stiffness: 120, damping: 25 }
    });
  };

  // Синхронизируем стейт зума с контроллером анимации (только при инициализации)
  useEffect(() => {
    // Устанавливаем начальный зум без изменения позиции
    mapControls.set({ scale: zoom });
    currentTransformRef.current.scale = zoom;
  }, []); // Только при монтировании компонента


  // Получаем упорядоченный список бойцов для чередования
  const getOrderedFighters = (): Fighter[] => {
    const aliveFighters = fighters.filter(f => f.health > 0);
    const playerFightersList = aliveFighters.filter(f => f.isPlayer).sort((a, b) => {
      return fighters.indexOf(a) - fighters.indexOf(b);
    });
    const enemyFightersList = aliveFighters.filter(f => !f.isPlayer).sort((a, b) => {
      return fighters.indexOf(a) - fighters.indexOf(b);
    });

    const ordered: Fighter[] = [];
    const maxLength = Math.max(playerFightersList.length, enemyFightersList.length);
    for (let i = 0; i < maxLength; i++) {
      if (i < playerFightersList.length) {
        ordered.push(playerFightersList[i]);
      }
      if (i < enemyFightersList.length) {
        ordered.push(enemyFightersList[i]);
      }
    }

    return ordered;
  };

  const getCurrentFighter = (): Fighter | null => {
    return fighters[currentFighterIndex] || null;
  };

  const getTurnQueue = (): Fighter[] => {
    const ordered = getOrderedFighters();
    if (ordered.length === 0) return [];

    const queue: Fighter[] = [];
    const currentFighter = getCurrentFighter();
    if (!currentFighter) return ordered;

    const currentIndex = ordered.findIndex(f => f.id === currentFighter.id);
    if (currentIndex === -1) return ordered;

    for (let i = 0; i < ordered.length; i++) {
      const nextIndex = (currentIndex + i) % ordered.length;
      queue.push(ordered[nextIndex]);
    }

    return queue;
  };

  const queueNumberMap = React.useMemo(() => {
    const ordered = getOrderedFighters();
    const mapObj = new Map<string, number>();
    ordered.forEach((fighter, index) => {
      mapObj.set(fighter.id, index + 1);
    });
    return mapObj;
  }, [fighters, currentFighterIndex]);

  const getFighterQueueNumber = (fighterId: string): number | null => {
    return queueNumberMap.get(fighterId) || null;
  };

  const FighterSprite: React.FC<{ fighter: Fighter }> = ({ fighter }) => {
    const [spriteError, setSpriteError] = React.useState(false);
    const spritePath = getFighterSpritePath(fighter);

    if (spriteError) {
      return <div className="fighter-icon-fallback">{fighter.isPlayer ? '⚔' : '👹'}</div>;
    }

    return (
      <img
        src={spritePath}
        alt={fighter.name}
        className="fighter-sprite"
        onError={() => setSpriteError(true)}
      />
    );
  };

  const findFreeCell = (mapToCheck: GameCell[][], startX: number, startY: number, occupiedCells?: Set<string>): { x: number; y: number } | null => {
    const isCellFree = (x: number, y: number): boolean => {
      if (!canMoveTo(mapToCheck, x, y)) return false;
      if (occupiedCells && occupiedCells.has(`${x},${y}`)) return false;
      return true;
    };

    if (isCellFree(startX, startY)) {
      return { x: startX, y: startY };
    }

    for (let radius = 1; radius < Math.max(MAP_WIDTH, MAP_HEIGHT); radius++) {
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          if (Math.abs(dx) === radius || Math.abs(dy) === radius) {
            const x = startX + dx;
            const y = startY + dy;
            if (x >= 0 && y >= 0 && y < mapToCheck.length && x < mapToCheck[0].length && isCellFree(x, y)) {
              return { x, y };
            }
          }
        }
      }
    }
    return null;
  };

  const generateAiFighters = (count: number): Fighter[] => {
    const aiFightersList: Fighter[] = [];
    const weaponCombos = [
      { left: 1, right: 3 }, { left: 2, right: null }, { left: 5, right: null },
      { left: 1, right: 4 }, { left: 6, right: null }, { left: 1, right: null },
    ];

    const shuffled = [...weaponCombos].sort(() => Math.random() - 0.5);

    for (let i = 0; i < count; i++) {
      const combo = shuffled[i % shuffled.length];
      const fighter = createFighter(`ai-${i}`, `Враг ${i + 1}`, 0, 0, false, combo.left, combo.right || undefined);
      aiFightersList.push(fighter);
    }
    return aiFightersList;
  };

  useEffect(() => {
    const newMap = generateRandomMap(MAP_WIDTH, MAP_HEIGHT);
    setMap(newMap);
    const newBackgroundLayer = generateBackgroundLayer(MAP_WIDTH, MAP_HEIGHT, BORDER_SIZE);
    setBackgroundLayer(newBackgroundLayer);

    const newFighters: Fighter[] = [];
    const occupiedCells = new Set<string>();

    const startY = Math.floor(MAP_HEIGHT / 2) - Math.floor(playerFighters.length / 2);
    playerFighters.forEach((fighter, index) => {
      const newFighter = { ...fighter };
      const baseX = Math.min(2, MAP_WIDTH - 5);
      const baseY = Math.max(0, Math.min(startY + index, MAP_HEIGHT - 1));

      const cell = findFreeCell(newMap, baseX, baseY, occupiedCells);
      if (cell) {
        newFighter.x = cell.x;
        newFighter.y = cell.y;
        occupiedCells.add(`${cell.x},${cell.y}`);
      } else {
        // Если не нашли свободную клетку, ищем любую свободную в радиусе
        let found = false;
        for (let searchRadius = 0; searchRadius < Math.max(MAP_WIDTH, MAP_HEIGHT) && !found; searchRadius++) {
          for (let dy = -searchRadius; dy <= searchRadius && !found; dy++) {
            for (let dx = -searchRadius; dx <= searchRadius && !found; dx++) {
              const x = baseX + dx;
              const y = baseY + dy;
              if (x >= 0 && y >= 0 && y < MAP_HEIGHT && x < MAP_WIDTH && !occupiedCells.has(`${x},${y}`) && canMoveTo(newMap, x, y)) {
                newFighter.x = x;
                newFighter.y = y;
                occupiedCells.add(`${x},${y}`);
                found = true;
              }
            }
          }
        }
        if (!found) {
          // Последний резерв - используем базовую позицию
          newFighter.x = baseX;
          newFighter.y = baseY;
          occupiedCells.add(`${baseX},${baseY}`);
        }
      }
      newFighter.isPlayer = true;

      // Пересчитываем maxStamina и maxHealth на основе ранга и веса экипировки
      let totalWeight = 0;
      if (newFighter.leftHand) totalWeight += newFighter.leftHand.weight;
      if (newFighter.rightHand) totalWeight += newFighter.rightHand.weight;
      if (newFighter.armor) totalWeight += newFighter.armor.weight;

      // Для людей: стамина зависит от ранга
      const baseStamina = 10 + (newFighter.rank || 0) * 2;
      newFighter.maxStamina = Math.max(1, baseStamina - totalWeight);
      newFighter.stamina = newFighter.maxStamina;

      // Здоровье зависит от ранга: ранг 0 = 10, ранг 1 = 12, ранг 2 = 14
      const newMaxHealth = getFighterMaxHealth(newFighter);
      newFighter.maxHealth = newMaxHealth;
      // Если текущее здоровье больше нового максимума, ограничиваем его
      if (newFighter.health > newMaxHealth) {
        newFighter.health = newMaxHealth;
      }

      newFighters.push(newFighter);
    });

    const aiFightersList = providedAiFighters || generateAiFighters(5);
    const aiStartY = Math.floor(MAP_HEIGHT / 2) - Math.floor(aiFightersList.length / 2);
    aiFightersList.forEach((fighter, index) => {
      const newFighter = { ...fighter };
      const baseX = Math.max(0, MAP_WIDTH - 3 - (index % 3));
      const baseY = Math.max(0, Math.min(aiStartY + index, MAP_HEIGHT - 1));

      let cell = findFreeCell(newMap, baseX, baseY, occupiedCells);
      let attempts = 0;
      while (cell && occupiedCells.has(`${cell.x},${cell.y}`) && attempts < 50) {
        cell = findFreeCell(newMap, baseX + (attempts % 5), baseY + Math.floor(attempts / 5), occupiedCells);
        attempts++;
      }

      if (cell && !occupiedCells.has(`${cell.x},${cell.y}`)) {
        newFighter.x = cell.x;
        newFighter.y = cell.y;
        occupiedCells.add(`${cell.x},${cell.y}`);
      } else {
        // Если не нашли свободную клетку, ищем любую свободную в радиусе
        let found = false;
        for (let searchRadius = 0; searchRadius < Math.max(MAP_WIDTH, MAP_HEIGHT) && !found; searchRadius++) {
          for (let dy = -searchRadius; dy <= searchRadius && !found; dy++) {
            for (let dx = -searchRadius; dx <= searchRadius && !found; dx++) {
              const x = baseX + dx;
              const y = baseY + dy;
              if (x >= 0 && y >= 0 && y < MAP_HEIGHT && x < MAP_WIDTH && !occupiedCells.has(`${x},${y}`) && canMoveTo(newMap, x, y)) {
                newFighter.x = x;
                newFighter.y = y;
                occupiedCells.add(`${x},${y}`);
                found = true;
              }
            }
          }
        }
        if (!found) {
          // Последний резерв - используем базовую позицию
          newFighter.x = baseX;
          newFighter.y = baseY;
          occupiedCells.add(`${baseX},${baseY}`);
        }
      }
      newFighter.isPlayer = false;

      // Пересчитываем maxStamina и maxHealth на основе ранга и веса экипировки
      let totalWeight = 0;
      if (newFighter.leftHand) totalWeight += newFighter.leftHand.weight;
      if (newFighter.rightHand) totalWeight += newFighter.rightHand.weight;
      if (newFighter.armor) totalWeight += newFighter.armor.weight;

      // Для орков: базовая стамина зависит от ранга (10/12/14), затем минус вес
      const orcBaseStamina = newFighter.rank === 0 ? 10 : newFighter.rank === 1 ? 12 : 14;
      newFighter.maxStamina = Math.max(1, orcBaseStamina - totalWeight);
      newFighter.stamina = newFighter.maxStamina;

      // Здоровье зависит от ранга: ранг 0 = 10, ранг 1 = 12, ранг 2 = 14
      const newMaxHealth = getFighterMaxHealth(newFighter);
      newFighter.maxHealth = newMaxHealth;
      // Если текущее здоровье больше нового максимума, ограничиваем его
      if (newFighter.health > newMaxHealth) {
        newFighter.health = newMaxHealth;
      }

      newFighters.push(newFighter);
    });

    setFighters(newFighters);
    updateMapWithFighters(newFighters);

    const playerFightersAliveList = newFighters.filter(f => f.isPlayer && f.health > 0);
    if (playerFightersAliveList.length > 0) {
      const firstPlayer = playerFightersAliveList[0];
      const firstIndex = newFighters.findIndex(f => f.id === firstPlayer.id);
      setCurrentFighterIndex(firstIndex);
      setIsAiTurn(false);
      // Увеличиваем задержку, чтобы карта успела инициализироваться
      setTimeout(() => {
        console.log('[GameBoard] useEffect: центрирование на первом бойце при начале боя', { fighterId: firstPlayer.id });
        centerOnFighter(firstPlayer);
      }, 300);
    }
  }, [playerFighters]);

  const updateMapWithFighters = (fightersList: Fighter[]) => {
    setMap(prevMap => {
      const newMap = prevMap.map(row => row.map(cell => ({ ...cell, fighter: null })));
      fightersList.forEach(fighter => {
        if (fighter.health > 0 && fighter.x >= 0 && fighter.y >= 0 && fighter.y < newMap.length && fighter.x < newMap[0].length) {
          (newMap[fighter.y][fighter.x] as any).fighter = fighter;
        }
      });
      return newMap;
    });
  };

  const handleCellClick = (x: number, y: number) => {
    // Игнорируем клик если был drag
    if (isDragging) {
      setIsDragging(false);
      return;
    }

    const currentFighter = getCurrentFighter();
    if (!currentFighter || !currentFighter.isPlayer || isAiTurn) return;
    if (currentFighter.health <= 0) return;

    const cell = map[y][x];

    if (cell.fighter && !cell.fighter.isPlayer) {
      const range = getFighterRange(currentFighter);
      if (canAttackInLine(currentFighter.x, currentFighter.y, x, y, range, map) && canFighterAttack(currentFighter)) {
        const actualEnemy = fighters.find(f => f.id === cell.fighter!.id);
        if (actualEnemy) {
          // Сохраняем состояние перед атакой
          if (!turnStartState) {
            setTurnStartState({
              fighter: currentFighter,
              position: { x: currentFighter.x, y: currentFighter.y },
              stamina: currentFighter.stamina,
              isDefending: currentFighter.isDefending
            });
          }
          setAttackedEnemyState({ id: actualEnemy.id, health: actualEnemy.health });
          setLastAction('attack');
          attackFighter(currentFighter, actualEnemy);
        }
        return;
      }
    }

    if (!cell.fighter && canMoveTo(map, x, y) && !isMoving) {
      const distance = getMoveDistance(currentFighter.x, currentFighter.y, x, y);
      if (distance <= currentFighter.stamina) {
        // Сохраняем состояние перед движением
        if (!turnStartState) {
          setTurnStartState({
            fighter: currentFighter,
            position: { x: currentFighter.x, y: currentFighter.y },
            stamina: currentFighter.stamina,
            isDefending: currentFighter.isDefending
          });
        }
        setLastAction('move');
        moveFighter(currentFighter, x, y);
      }
    }

    setSelectedCell({ x, y });
  };

  // Вычисляет стоимость пути
  const calculatePathCost = (path: Array<{ x: number; y: number }>, currentMap: GameCell[][]): number => {
    if (path.length === 0) return 0;
    let cost = 0;
    for (const step of path) {
      const cell = currentMap[step.y]?.[step.x];
      if (cell) {
        cost += getMovementCost(cell);
      } else {
        cost += 1; // По умолчанию 1, если клетка не найдена
      }
    }
    return cost;
  };

  // Вычисляет подсветку клеток для текущего союзного юнита
  const calculateMovementHighlights = useCallback(() => {
    const currentFighter = getCurrentFighter();
    if (!currentFighter || !currentFighter.isPlayer || isAiTurn || isMoving) {
      setMovementHighlights(new Map());
      return;
    }

    const highlights = new Map<string, 'move-with-attack' | 'move-max' | 'blocked'>();
    const attackCost = getAttackCost(currentFighter);
    const maxStamina = currentFighter.stamina;
    const moveWithAttackStamina = maxStamina - attackCost;

    // Проверяем все клетки на карте
    for (let y = 0; y < MAP_HEIGHT; y++) {
      for (let x = 0; x < MAP_WIDTH; x++) {
        const cellKey = `${x},${y}`;

        // Пропускаем текущую позицию
        if (x === currentFighter.x && y === currentFighter.y) {
          continue;
        }

        // Проверяем, можно ли пройти на эту клетку
        if (!canMoveTo(map, x, y)) {
          highlights.set(cellKey, 'blocked');
          continue;
        }

        // Вычисляем путь до этой клетки
        const path = calculatePath(currentFighter.x, currentFighter.y, x, y, map);
        if (path.length === 0) {
          highlights.set(cellKey, 'blocked');
          continue;
        }

        // Вычисляем стоимость пути
        const pathCost = calculatePathCost(path, map);

        if (pathCost <= moveWithAttackStamina) {
          // Можно сходить, оставив стамину для удара
          highlights.set(cellKey, 'move-with-attack');
        } else if (pathCost <= maxStamina) {
          // Можно сходить, но не хватит стамины для удара
          highlights.set(cellKey, 'move-max');
        } else {
          // Нельзя сходить (не хватает стамины)
          highlights.set(cellKey, 'blocked');
        }
      }
    }

    setMovementHighlights(highlights);
  }, [fighters, currentFighterIndex, isAiTurn, isMoving, map, MAP_WIDTH, MAP_HEIGHT]);

  // Обновляем подсветку при изменении текущего юнита или карты
  useEffect(() => {
    calculateMovementHighlights();
  }, [calculateMovementHighlights]);

  const moveFighter = (fighter: Fighter, newX: number, newY: number) => {
    if (isMoving) return;
    setIsMoving(true);
    const path = calculatePath(fighter.x, fighter.y, newX, newY, map);
    if (path.length === 0) {
      setIsMoving(false);
      return;
    }

    let totalStaminaCost = 0;

    let currentStep = 0;
    const animate = () => {
      if (currentStep >= path.length) {
        // Не обновляем turnStartState.position - она должна оставаться начальной для отмены
        setIsMoving(false);
        return;
      }
      const step = path[currentStep];
      setFighters(prev => {
        const cost = getMovementCost(map[step.y][step.x]);
        totalStaminaCost += cost;
        const updated = prev.map(f => f.id === fighter.id ? { ...f, x: step.x, y: step.y, stamina: Math.max(0, f.stamina - cost) } : f);
        updateMapWithFighters(updated);
        return updated;
      });
      currentStep++;
      setTimeout(animate, 150);
    };
    animate();
  };

  const attackFighter = (attacker: Fighter, defender: Fighter) => {
    const actualAttacker = fighters.find(f => f.id === attacker.id) || attacker;
    const actualDefender = fighters.find(f => f.id === defender.id) || defender;




    const dmg = Math.max(0, getFighterAttack(actualAttacker) - getFighterDefense(actualDefender));
    if (dmg > 0) {
      setBattleLog(prev => [...prev, { attacker: actualAttacker.name, target: actualDefender.name, damage: dmg, timestamp: Date.now() }]);
    }
    triggerAttackEffects(actualDefender.x, actualDefender.y, actualDefender.id, dmg);

    setTimeout(() => {
      setFighters(prev => {
        const next = prev.map(f => {
          if (f.id === actualAttacker.id) return { ...f, stamina: f.stamina - getAttackCost(f) };
          if (f.id === actualDefender.id) return { ...f, health: Math.max(0, f.health - dmg) };
          return f;
        });
        updateMapWithFighters(next);

        const playersAlive = next.filter(f => f.isPlayer && f.health > 0);
        const aiAlive = next.filter(f => !f.isPlayer && f.health > 0);
        if (playersAlive.length === 0) onGameOver('ai', next);
        else if (aiAlive.length === 0) onGameOver('player', next);

        return next;
      });
    }, 200);
  };

  // Hooks moved to top


  const handleUseRedPotion = () => {
    const currentFighter = getCurrentFighter();
    if (!currentFighter || !currentFighter.isPlayer || isAiTurn) return;
    if (currentFighter.health >= getFighterMaxHealth(currentFighter)) return; // Уже полностью здоров

    if (usePotion('redPotion')) {
      setFighters(prev => {
        const updated = prev.map(f =>
          f.id === currentFighter.id
            ? { ...f, health: getFighterMaxHealth(f) }
            : f
        );
        updateMapWithFighters(updated);
        return updated;
      });
    }
  };

  const toggleDefense = () => {
    const currentFighter = getCurrentFighter();
    if (!currentFighter || !currentFighter.isPlayer || isAiTurn) return;
    const cost = getDefenseCost(currentFighter);

    // Сохраняем состояние перед изменением щита
    if (!turnStartState) {
      setTurnStartState({
        fighter: currentFighter,
        position: { x: currentFighter.x, y: currentFighter.y },
        stamina: currentFighter.stamina,
        isDefending: currentFighter.isDefending
      });
    }

    if (currentFighter.isDefending) {
      setFighters(prev => {
        const next = prev.map(f => f.id === currentFighter.id ? { ...f, isDefending: false } : f);
        updateMapWithFighters(next);
        return next;
      });
    } else if (cost > 0 && currentFighter.stamina >= cost) {
      setFighters(prev => {
        const next = prev.map(f => f.id === currentFighter.id ? { ...f, isDefending: true, stamina: f.stamina - cost } : f);
        updateMapWithFighters(next);
        return next;
      });
    }
  };

  // Сохраняем начальное состояние при начале хода игрока и сбрасываем щит
  React.useEffect(() => {
    const currentFighter = getCurrentFighter();

    // Сбрасываем щит при смене юнита (начале хода нового юнита)
    if (currentFighter && prevFighterIndexRef.current !== currentFighterIndex) {
      const fighterInState = fighters.find(f => f.id === currentFighter.id);
      if (fighterInState && fighterInState.isDefending) {
        setFighters(prev => {
          const updated = prev.map(f =>
            f.id === currentFighter.id ? { ...f, isDefending: false } : f
          );
          updateMapWithFighters(updated);
          return updated;
        });
      }
      prevFighterIndexRef.current = currentFighterIndex;

      // Сбрасываем состояние отмены только при смене юнита (начале нового хода)
      if (currentFighter) {
        if (currentFighter.isPlayer && !isAiTurn) {
          const actualFighter = fighters.find(f => f.id === currentFighter.id) || currentFighter;
          setTurnStartState({
            fighter: actualFighter,
            position: { x: actualFighter.x, y: actualFighter.y },
            stamina: actualFighter.stamina,
            isDefending: actualFighter.isDefending
          });
          setLastAction(null);
          setAttackedEnemyState(null);
        } else {
          setTurnStartState(null);
          setLastAction(null);
          setAttackedEnemyState(null);
        }
      } else {
        setTurnStartState(null);
        setLastAction(null);
        setAttackedEnemyState(null);
      }
    }
  }, [currentFighterIndex, isAiTurn]);

  const undoTurn = () => {
    if (!turnStartState) return;

    const currentFighter = getCurrentFighter();
    if (!currentFighter || currentFighter.id !== turnStartState.fighter.id) return;

    // Отменяем последнее действие
    if (lastAction === 'move') {
      // Возвращаем юнита на начальную позицию
      setFighters(prev => {
        const updated = prev.map(f =>
          f.id === turnStartState.fighter.id
            ? {
              ...f,
              x: turnStartState.position.x,
              y: turnStartState.position.y,
              stamina: turnStartState.stamina,
              isDefending: turnStartState.isDefending
            }
            : f
        );
        updateMapWithFighters(updated);
        return updated;
      });
      setLastAction(null);
    } else if (lastAction === 'attack' && attackedEnemyState) {
      // Отменяем атаку - возвращаем стамину, состояние и здоровье врага
      setFighters(prev => {
        const updated = prev.map(f => {
          if (f.id === turnStartState.fighter.id) {
            return {
              ...f,
              stamina: turnStartState.stamina,
              isDefending: turnStartState.isDefending
            };
          }
          if (f.id === attackedEnemyState.id) {
            return { ...f, health: attackedEnemyState.health };
          }
          return f;
        });
        updateMapWithFighters(updated);
        return updated;
      });
      setLastAction(null);
      setAttackedEnemyState(null);
    } else {
      // Возвращаем состояние и позицию (если позиция изменилась, но lastAction не установлен)
      setFighters(prev => {
        const updated = prev.map(f =>
          f.id === turnStartState.fighter.id
            ? {
              ...f,
              x: turnStartState.position.x,
              y: turnStartState.position.y,
              stamina: turnStartState.stamina,
              isDefending: turnStartState.isDefending
            }
            : f
        );
        updateMapWithFighters(updated);
        return updated;
      });
      setLastAction(null);
    }
  };

  const endTurn = () => {
    setFighters(prev => {
      const current = prev[currentFighterIndex];
      if (!current) {
        updateMapWithFighters(prev);
        return prev;
      }
      // Не сбрасываем isDefending здесь - он будет сброшен при начале следующего хода этого юнита
      const updated = prev.map(f => f.id === current.id ? { ...f, stamina: f.maxStamina } : f);
      updateMapWithFighters(updated);
      return updated;
    });

    setTimeout(() => {
      setFighters(prev => {
        const ordered = getOrderedFightersFromList(prev);
        if (ordered.length === 0) {
          updateMapWithFighters(prev);
          return prev;
        }

        const current = prev[currentFighterIndex];
        let nextFighter;

        if (!current) {
          nextFighter = ordered[0];
        } else {
          const currentIndex = ordered.findIndex(f => f.id === current.id);
          const nextIndex = (currentIndex + 1) % ordered.length;
          nextFighter = ordered[nextIndex];
        }

        const nextIndexInFighters = prev.findIndex(f => f.id === nextFighter.id);
        setCurrentFighterIndex(nextIndexInFighters);
        setIsAiTurn(!nextFighter.isPlayer);
        // Убеждаемся, что карта синхронизирована перед следующим ходом
        updateMapWithFighters(prev);
        return prev;
      });
    }, 0);
  };

  // Добавляем обработчик горячих клавиш
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Пробел для пропуска хода
      if (e.code === 'Space') {
        // Если сейчас ход игрока (не ИИ) и текущий боец - игрок
        const currentFighter = fighters[currentFighterIndex];
        if (!isAiTurn && currentFighter?.isPlayer) {
          // Предотвращаем прокрутку страницы
          e.preventDefault();
          endTurn();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentFighterIndex, fighters, isAiTurn, endTurn]);

  const getOrderedFightersFromList = (list: Fighter[]): Fighter[] => {
    const alive = list.filter(f => f.health > 0);
    const players = alive.filter(f => f.isPlayer).sort((a, b) => list.indexOf(a) - list.indexOf(b));
    const enemies = alive.filter(f => !f.isPlayer).sort((a, b) => list.indexOf(a) - list.indexOf(b));

    const ordered: Fighter[] = [];
    const max = Math.max(players.length, enemies.length);
    for (let i = 0; i < max; i++) {
      if (i < players.length) ordered.push(players[i]);
      if (i < enemies.length) ordered.push(enemies[i]);
    }
    return ordered;
  };

  // Ход ИИ
  useEffect(() => {
    if (!isAiTurn || fighters.length === 0) return;

    const currentFighter = fighters[currentFighterIndex];
    if (!currentFighter || currentFighter.isPlayer || currentFighter.health <= 0) {
      if (currentFighter && currentFighter.health <= 0) setTimeout(() => endTurn(), 100);
      return;
    }

    // Автоцентрирование камеры на враге
    setTimeout(() => centerOnFighter(currentFighter), 300);

    const timer = setTimeout(() => {
      setFighters(latestFighters => {
        const fighterActual = latestFighters[currentFighterIndex];
        if (!fighterActual || fighterActual.isPlayer || fighterActual.health <= 0) {
          setTimeout(() => endTurn(), 100);
          return latestFighters;
        }

        const action = aiTurn(fighterActual, latestFighters, map);

        if (action.type === 'move') {
          const path = calculatePath(fighterActual.x, fighterActual.y, action.x, action.y, map);
          if (path.length === 0) {
            setTimeout(() => endTurn(), 100);
            return latestFighters;
          }

          let currentStep = 0;
          const animateMove = () => {
            if (currentStep >= path.length) {
              setFighters(afterMove => {
                const f = afterMove.find(u => u.id === fighterActual.id);
                if (!f) {
                  updateMapWithFighters(afterMove);
                  setTimeout(() => endTurn(), 100);
                  return afterMove;
                }

                // Центрируем камеру на противнике после завершения движения
                centerOnFighter(f);

                const enemies = afterMove.filter(u => u.isPlayer && u.health > 0);
                const target = enemies.find(e => {
                  const r = getFighterRange(f);
                  return canAttackInLine(f.x, f.y, e.x, e.y, r) && canFighterAttack(f);
                });

                if (target) {
                  const result = performAiAttacksHelper(f, target, afterMove);
                  // Убеждаемся, что карта обновлена с финальным состоянием
                  updateMapWithFighters(result);
                  return result;
                } else {
                  updateMapWithFighters(afterMove);
                  setTimeout(() => endTurn(), 500);
                  return afterMove;
                }
              });
              return;
            }

            const step = path[currentStep];
            setFighters(p => {
              const cost = getMovementCost(map[step.y][step.x]);
              const next = p.map(u => u.id === fighterActual.id ? { ...u, x: step.x, y: step.y, stamina: Math.max(0, u.stamina - cost) } : u);
              updateMapWithFighters(next);

              // Центрируем камеру на противнике во время движения
              const updatedFighter = next.find(u => u.id === fighterActual.id);
              if (updatedFighter) {
                centerOnFighter(updatedFighter);
              }

              return next;
            });
            currentStep++;
            setTimeout(animateMove, 150);
          };
          animateMove();
        } else if (action.type === 'attack') {
          const target = latestFighters.find(u => u.id === action.targetId);
          if (target) {
            const result = performAiAttacksHelper(fighterActual, target, latestFighters);
            // Убеждаемся, что карта обновлена с финальным состоянием
            updateMapWithFighters(result);
            return result;
          } else {
            updateMapWithFighters(latestFighters);
            setTimeout(() => endTurn(), 100);
          }
        } else if (action.type === 'defend') {
          const updated = latestFighters.map(u => u.id === fighterActual.id ? { ...u, isDefending: true, stamina: u.stamina - getDefenseCost(u) } : u);
          updateMapWithFighters(updated);
          setTimeout(() => endTurn(), 500);
          return updated;
        } else {
          updateMapWithFighters(latestFighters);
          setTimeout(() => endTurn(), 100);
        }
        return latestFighters;
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [isAiTurn, currentFighterIndex]);

  const performAiAttacksHelper = (attacker: Fighter, target: Fighter, currentList: Fighter[]): Fighter[] => {
    let aState = attacker;
    let tState = target;
    let list = currentList;

    while (canFighterAttack(aState) && canAttackInLine(aState.x, aState.y, tState.x, tState.y, getFighterRange(aState))) {
      const dmg = Math.max(0, getFighterAttack(aState) - getFighterDefense(tState));
      if (dmg > 0) {
        setBattleLog(prev => [...prev, { attacker: aState.name, target: tState.name, damage: dmg, timestamp: Date.now() }]);
      }
      triggerAttackEffects(tState.x, tState.y, tState.id, dmg);


      list = list.map(f => {
        if (f.id === aState.id) return { ...f, stamina: f.stamina - getAttackCost(f) };
        if (f.id === tState.id) return { ...f, health: Math.max(0, f.health - dmg) };
        return f;
      });

      aState = list.find(f => f.id === aState.id)!;
      tState = list.find(f => f.id === tState.id)!;
      if (tState.health <= 0) break;
    }

    // НЕ вызываем updateMapWithFighters здесь - это будет сделано в вызывающем коде
    // чтобы гарантировать синхронизацию с состоянием fighters
    const pAlive = list.filter(f => f.isPlayer && f.health > 0);
    const aAlive = list.filter(f => !f.isPlayer && f.health > 0);
    if (pAlive.length === 0) setTimeout(() => onGameOver('ai', list), 500);
    else if (aAlive.length === 0) setTimeout(() => onGameOver('player', list), 500);
    else setTimeout(() => endTurn(), 500);

    return list;
  };

  const currentFighter = getCurrentFighter();
  const playerFightersAlive = fighters.filter(f => f.isPlayer && f.health > 0).length;
  const aiFightersAlive = fighters.filter(f => !f.isPlayer && f.health > 0).length;

  return (
    <div className={`game-board ${hoveredEnemy ? 'enemy-hovered' : ''}`}>
      <aside className="side-panel left">
        <div className="panel-content">
          <div className="info-buttons vertical">
            {currentFighter && (
              <div className="current-fighter-info in-side-panel">
                <span>{currentFighter.isPlayer ? currentFighter.name : t.getEnemyName(currentFighter.name)}</span>
                <span>🔋 {currentFighter.stamina}/{currentFighter.maxStamina}</span>
                <span>❤️ {currentFighter.health}/{currentFighter.maxHealth}</span>
              </div>
            )}

            <div className="unit-counts-vertical">
              <div className="count-item">
                <span className="label text-yellow-500">{t.battle.you}</span>
                <span className="value">{playerFightersAlive}</span>
              </div>
              <div className="count-item">
                <span className="label text-red-500">{t.battle.them}</span>
                <span className="value">{aiFightersAlive}</span>
              </div>
            </div>

            <div className="controls-row">
              <button className="info-toggle-btn zoom-btn" onClick={handleZoomIn} title={t.game.zoomIn}>+</button>
              <button className="info-toggle-btn zoom-btn centered" onClick={() => currentFighter && centerOnFighter(currentFighter)} title={t.game.centerOnFighter}>🎯</button>
              <button className="info-toggle-btn zoom-btn" onClick={handleZoomOut} title={t.game.zoomOut}>-</button>
            </div>

            <button className={`info-toggle-btn ${showCoords ? 'active' : ''}`} onClick={() => setShowCoords(!showCoords)}>
              {t.game.grid} {showCoords ? t.game.gridOn : t.game.gridOff}
            </button>
            <button className="info-toggle-btn" onClick={() => setIsQueueCollapsed(!isQueueCollapsed)}>
              {t.battle.queue} {isQueueCollapsed ? '▼' : '▲'}
            </button>
            <button className="info-toggle-btn" onClick={() => setIsLogCollapsed(!isLogCollapsed)}>
              {t.battle.log} {isLogCollapsed ? '▼' : '▲'}
            </button>
          </div>

          {!isQueueCollapsed && (
            <div className="turn-queue side">
              {getTurnQueue().map((f, i) => (
                <div key={f.id} className={`queue-item compact ${f.isPlayer ? 'player' : 'enemy'} ${hoveredFighterId === f.id ? 'highlighted' : ''}`}
                  onMouseEnter={() => setHoveredFighterId(f.id)} onMouseLeave={() => setHoveredFighterId(null)}>
                  <span className="queue-number">{i + 1}</span>
                  <span className="queue-icon">{f.isPlayer ? '⚔' : '👹'}</span>
                </div>
              ))}
            </div>
          )}

          {!isLogCollapsed && (
            <div className="battle-log side">
              {battleLog.slice(-10).reverse().map((log, i) => {
                // Пытаемся перевести имена если это враги
                const attackerName = log.attacker.includes('#') || !fighters.find(f => f.name === log.attacker)?.isPlayer
                  ? t.getEnemyName(log.attacker)
                  : log.attacker;
                const targetName = log.target.includes('#') || !fighters.find(f => f.name === log.target)?.isPlayer
                  ? t.getEnemyName(log.target)
                  : log.target;

                return (
                  <div key={i} className="log-entry compact">
                    <div className="log-names">{attackerName} ⚔️ {targetName}</div>
                    <div className="log-damage">-{log.damage} HP</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </aside>

      <div className="main-battle-area">
        <div className="game-header">
          <div className="game-info">
            {currentFighter && (
              <div className="current-fighter-info" data-tutorial="currentFighterInfo">
                <span>{currentFighter.isPlayer ? currentFighter.name : t.getEnemyName(currentFighter.name)}</span>
                <span>🔋 {t.game.stamina}: {currentFighter.stamina}/{currentFighter.maxStamina}</span>
                <span>❤️ {t.game.health}: {currentFighter.health}/{currentFighter.maxHealth}</span>
              </div>
            )}
          </div>

          <div className="info-buttons mobile-only">
            <div className="controls-row">
              <button className="info-toggle-btn zoom-btn" onClick={handleZoomIn}>+</button>
              <button className="info-toggle-btn zoom-btn centered" data-tutorial="centerButton" onClick={() => currentFighter && centerOnFighter(currentFighter)}>🎯</button>
              <button className="info-toggle-btn zoom-btn" onClick={handleZoomOut}>-</button>
            </div>

            <div className="unit-counts-vertical compact" data-tutorial="unitCounts">
              <div className="count-item">
                <span className="label">{t.game.yourUnits}:</span>
                <span className="value">{playerFightersAlive}</span>
              </div>
              <div className="count-item">
                <span className="label">{t.game.enemyUnits}:</span>
                <span className="value">{aiFightersAlive}</span>
              </div>
            </div>

            <button className={`info-toggle-btn ${showCoords ? 'active' : ''}`} data-tutorial="gridButton" onClick={() => setShowCoords(!showCoords)}>
              {t.game.grid}
            </button>
            <button className="info-toggle-btn" data-tutorial="queueToggleButton" onClick={() => setIsQueueCollapsed(!isQueueCollapsed)}>
              {t.game.queue} {isQueueCollapsed ? '▼' : '▲'}
            </button>
            <button className="info-toggle-btn" data-tutorial="logToggleButton" onClick={() => setIsLogCollapsed(!isLogCollapsed)}>
              {t.game.log} {isLogCollapsed ? '▼' : '▲'}
            </button>
          </div>
        </div>

        <div className="side-panel-wrapper mobile-only">
          {!isQueueCollapsed && (
            <div className="turn-queue collapsible" data-tutorial="turnQueue">
              <div className="queue-list">
                {getTurnQueue().map((f, i) => (
                  <div key={f.id} className={`queue-item ${f.isPlayer ? 'player' : 'enemy'} ${hoveredFighterId === f.id ? 'highlighted' : ''}`}
                    onMouseEnter={() => setHoveredFighterId(f.id)} onMouseLeave={() => setHoveredFighterId(null)}>
                    <span className="queue-number">{i + 1}</span>
                    <span className="queue-icon">{f.isPlayer ? '⚔' : '👹'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="map-container" ref={containerRef}>
          <div className="map-wrapper">
            <motion.div
              ref={mapElementRef}
              className="background-grid"
              style={{
                gridTemplateColumns: `repeat(${MAP_WIDTH + BORDER_SIZE * 2}, 1fr)`,
                transformOrigin: '0 0'
              }}
              animate={mapControls}
              initial={{ scale: zoom, x: 0, y: 0 }}
              drag dragElastic={0} dragMomentum={true}
              onDragStart={(_event, info) => {
                setIsDragging(false);
                dragStartRef.current = { x: info.point.x, y: info.point.y };

                // Синхронизируем currentTransformRef из DOM перед сохранением начальной позиции
                // Это важно для правильной работы первого drag
                if (mapElementRef.current) {
                  const computedStyle = window.getComputedStyle(mapElementRef.current);
                  const transform = computedStyle.transform;
                  if (transform && transform !== 'none') {
                    const matrix = transform.match(/matrix\(([^)]+)\)/);
                    if (matrix) {
                      const values = matrix[1].split(',').map(v => parseFloat(v.trim()));
                      if (values.length >= 6) {
                        currentTransformRef.current.x = values[4] || 0;
                        currentTransformRef.current.y = values[5] || 0;
                      }
                    }
                  }
                }

                // Сохраняем начальную позицию карты
                dragStartPositionRef.current = {
                  x: currentTransformRef.current.x,
                  y: currentTransformRef.current.y
                };
              }}
              onDrag={(_event, info) => {
                if (dragStartRef.current) {
                  const dx = Math.abs(info.point.x - dragStartRef.current.x);
                  const dy = Math.abs(info.point.y - dragStartRef.current.y);
                  if (dx > 5 || dy > 5) {
                    setIsDragging(true);
                  }
                }
              }}
              dragConstraints={getDragConstraints}
              onDragEnd={(_event, info) => {
                // Вычисляем новую позицию на основе начальной позиции и смещения
                if (dragStartPositionRef.current && info.offset) {
                  const newX = dragStartPositionRef.current.x + info.offset.x;
                  const newY = dragStartPositionRef.current.y + info.offset.y;
                  const constrained = constrainMapPosition(newX, newY);

                  // Обновляем ref и применяем ограниченную позицию
                  currentTransformRef.current = {
                    x: constrained.x,
                    y: constrained.y,
                    scale: currentTransformRef.current.scale
                  };

                  // Если позиция вышла за границы, корректируем её с анимацией
                  if (Math.abs(constrained.x - newX) > 1 || Math.abs(constrained.y - newY) > 1) {
                    mapControls.start({
                      x: constrained.x,
                      y: constrained.y,
                      transition: { type: 'spring', stiffness: 300, damping: 30 }
                    });
                  }
                } else {
                  // Fallback: корректируем текущую позицию из ref
                  const constrained = constrainMapPosition(
                    currentTransformRef.current.x,
                    currentTransformRef.current.y
                  );

                  if (Math.abs(constrained.x - currentTransformRef.current.x) > 1 ||
                    Math.abs(constrained.y - currentTransformRef.current.y) > 1) {
                    currentTransformRef.current = {
                      x: constrained.x,
                      y: constrained.y,
                      scale: currentTransformRef.current.scale
                    };
                    mapControls.start({
                      x: constrained.x,
                      y: constrained.y,
                      transition: { type: 'spring', stiffness: 300, damping: 30 }
                    });
                  }
                }

                // Небольшая задержка перед сбросом, чтобы onClick не сработал
                setTimeout(() => {
                  setIsDragging(false);
                  dragStartRef.current = null;
                  dragStartPositionRef.current = null;
                }, 50);
              }}
              onAnimationComplete={(definition) => {
                // Обновляем ref после завершения анимации (включая перетаскивание)
                if (definition && typeof definition === 'object') {
                  if ('x' in definition) currentTransformRef.current.x = (definition.x as number) || 0;
                  if ('y' in definition) currentTransformRef.current.y = (definition.y as number) || 0;
                  if ('scale' in definition) currentTransformRef.current.scale = (definition.scale as number) || zoom;
                }
              }}>

              {/* Фоновый слой */}
              {backgroundLayer.map((row, bgY) => row.map((bgCell, bgX) => {
                // Проверяем, находится ли клетка в области игрового поля
                const isGameArea = bgX >= BORDER_SIZE && bgX < MAP_WIDTH + BORDER_SIZE &&
                  bgY >= BORDER_SIZE && bgY < MAP_HEIGHT + BORDER_SIZE;

                // Пропускаем клетки игрового поля (они будут отображены отдельно)
                if (isGameArea) {
                  return null;
                }

                return (
                  <div
                    key={`bg-${bgX}-${bgY}`}
                    className={`cell background-cell ${bgCell.type}`}
                    style={{ pointerEvents: 'none' }}
                  >
                    <img src={`./sprites/map/${bgCell.type}.png`} className="terrain-sprite" alt="" onError={(evt) => { evt.currentTarget.style.display = 'none' }} />
                  </div>
                );
              }))}

              {/* Игровое поле с красной рамкой */}
              <div
                className="game-grid"
                style={{
                  gridColumn: `${BORDER_SIZE + 1} / ${BORDER_SIZE + MAP_WIDTH + 1}`,
                  gridRow: `${BORDER_SIZE + 1} / ${BORDER_SIZE + MAP_HEIGHT + 1}`,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${MAP_WIDTH}, 1fr)`,
                  gap: '2px',
                  outline: '3px solid red',
                  outlineOffset: '0px',
                  boxSizing: 'border-box'
                }}
              >
                {/* Render Floating Damage Indicators */}
                {damageIndicators.map(indicator => (
                  <div
                    key={indicator.id}
                    className="floating-damage"
                    style={{
                      position: 'absolute',
                      left: `${(indicator.x + BORDER_SIZE) * 107 + 52}px`, // 105 cell + 2 gap, centered roughly, с учетом фонового слоя
                      top: `${(indicator.y + BORDER_SIZE) * 107 + 20}px`
                    }}
                  >
                    -{indicator.damage}
                  </div>
                ))}

                {map.map((row, y) => row.map((cell, x) => {
                  const f = cell.fighter;
                  const isCurrent = f && currentFighter && f.id === currentFighter.id;
                  const isS = selectedCell && selectedCell.x === x && selectedCell.y === y;
                  const r = currentFighter ? getFighterRange(currentFighter) : 0;
                  const canAtk = currentFighter && canAttackInLine(currentFighter.x, currentFighter.y, x, y, r, map);
                  let dmg = 0;
                  if (currentFighter && currentFighter.isPlayer && !isAiTurn && f && !f.isPlayer && canAtk && canFighterAttack(currentFighter)) {
                    dmg = Math.max(0, getFighterAttack(currentFighter) - getFighterDefense(f));
                  }
                  const isShaking = shakingCell && shakingCell.x === x && shakingCell.y === y;

                  // Получаем тип подсветки для этой клетки
                  const cellKey = `${x},${y}`;
                  const highlightType = movementHighlights.get(cellKey);
                  const highlightClass = highlightType === 'move-with-attack' ? 'movement-highlight-attack' :
                    highlightType === 'move-max' ? 'movement-highlight-max' :
                      highlightType === 'blocked' ? 'movement-highlight-blocked' : '';

                  return (
                    <div key={`${x}-${y}`} className={`cell ${cell.type} ${f ? 'has-fighter' : ''} ${isCurrent ? 'current-fighter' : ''} ${isS ? 'selected' : ''} ${canAtk && f && !f.isPlayer ? 'attackable' : ''} ${isCurrent && f.isPlayer ? 'current-player-turn' : ''} ${isCurrent && !f.isPlayer ? 'current-enemy-turn' : ''} ${isShaking ? 'shake-cell' : ''} ${highlightClass}`}
                      onClick={() => handleCellClick(x, y)}
                      onMouseEnter={() => { if (f && !f.isPlayer && dmg > 0) setHoveredEnemy({ fighter: f, damage: dmg }); }}
                      onMouseLeave={() => setHoveredEnemy(null)}>
                      <img src={`./sprites/map/${cell.type}.png`} className="terrain-sprite" alt="" onError={(evt) => { evt.currentTarget.style.display = 'none' }} />
                      {(showCoords || !f) && <div className={`cell-coords ${showCoords ? 'visible-always' : ''}`}>{String.fromCharCode(65 + y)}{x + 1}</div>}
                      {f && (
                        <div className={`fighter ${f.isPlayer ? 'player' : 'enemy'} ${f.health <= 0 ? 'dead' : ''} ${hoveredFighterId === f.id ? 'highlighted' : ''} ${flashingFighterId === f.id ? 'flash-red' : ''}`}>
                          <div className="fighter-stamina">{f.stamina}</div>
                          {(() => { const q = getFighterQueueNumber(f.id); return q !== null ? <div className="fighter-queue-number">{q}</div> : null; })()}
                          <FighterSprite fighter={f} />
                          <div className="fighter-health">❤️{f.health}</div>
                          {(() => {
                            const passiveDefense = getFighterPassiveDefense(f);
                            const activeDefense = f.isDefending ? getFighterActiveDefense(f) : 0;
                            const totalDefense = passiveDefense + activeDefense;
                            return totalDefense > 0 ? (
                              <div className="armor-indicator">{totalDefense}</div>
                            ) : null;
                          })()}
                          {(() => {
                            const attack = getFighterAttack(f);
                            return attack > 0 ? (
                              <div className="attack-indicator">{attack}</div>
                            ) : null;
                          })()}
                          {dmg > 0 && <div className="damage-preview">-{dmg}</div>}
                        </div>
                      )}
                    </div>
                  );
                }))}
              </div>
            </motion.div>
          </div>
        </div>

        {currentFighter && currentFighter.isPlayer && !isAiTurn && (
          <div className="action-panel mobile-only">
            <button onClick={toggleDefense} disabled={getDefenseCost(currentFighter) === 0 || (!currentFighter.isDefending && currentFighter.stamina < getDefenseCost(currentFighter))} className="action-btn">
              {currentFighter.isDefending ? t.game.removeShield : `${t.game.shield}(${getDefenseCost(currentFighter)})`}
            </button>
            <button
              onClick={handleUseRedPotion}
              disabled={(potions?.redPotion || 0) === 0 || currentFighter.health >= getFighterMaxHealth(currentFighter)}
              className="action-btn"
              title={t.game.redPotionTooltip}
            >
              ❤️ {potions?.redPotion || 0}
            </button>
            <button
              onClick={undoTurn}
              disabled={
                !turnStartState ||
                (!lastAction &&
                  currentFighter.x === turnStartState.position.x &&
                  currentFighter.y === turnStartState.position.y &&
                  currentFighter.stamina === turnStartState.stamina &&
                  currentFighter.isDefending === turnStartState.isDefending)
              }
              className="action-btn"
            >
              {t.game.undo}
            </button>
            <button onClick={endTurn} className="action-btn primary">{t.game.endTurn}</button>
            <button onClick={() => onGameOver('ai', fighters)} className="action-btn retreat">{t.game.retreat}</button>
          </div>
        )}

        {isAiTurn && <div className="ai-turn-indicator">{t.game.enemyTurn}</div>}
      </div>

      <aside className="side-panel right">
        {currentFighter && currentFighter.isPlayer && !isAiTurn && (
          <div className="action-panel-vertical" data-tutorial="actionPanel">
            <button onClick={endTurn} className="action-btn primary vertical" data-tutorial="endTurnButton">{t.game.endTurn}</button>
            <button onClick={toggleDefense} disabled={getDefenseCost(currentFighter) === 0 || (!currentFighter.isDefending && currentFighter.stamina < getDefenseCost(currentFighter))} className="action-btn vertical" data-tutorial="defenseButton">
              {currentFighter.isDefending ? t.game.removeShield : t.game.shield}
            </button>
            <button
              onClick={handleUseRedPotion}
              disabled={(potions?.redPotion || 0) === 0 || currentFighter.health >= getFighterMaxHealth(currentFighter)}
              className="action-btn vertical"
              data-tutorial="potionButton"
              title={t.game.redPotionTooltip}
            >
              {t.game.redPotion} {potions?.redPotion || 0}
            </button>
            <button
              onClick={undoTurn}
              disabled={
                !turnStartState ||
                (currentFighter.x === turnStartState.position.x &&
                  currentFighter.y === turnStartState.position.y &&
                  currentFighter.stamina === turnStartState.stamina &&
                  currentFighter.isDefending === turnStartState.isDefending)
              }
              className="action-btn vertical"
              data-tutorial="undoButton"
            >
              {t.game.undo}
            </button>
            <button onClick={() => onGameOver('ai', fighters)} className="action-btn retreat vertical" data-tutorial="retreatButton">{t.game.retreat}</button>
          </div>
        )}
      </aside>
    </div>
  );
};

export default GameBoard;
